import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { UserButton, useAuth } from "@clerk/clerk-react";
import React, { useEffect } from "react";
import { ModeToggle } from "./mode-toggle";

interface SiteHeaderProps {
  className?: string;
  title?: string;
}

// Hook to check if we're within a SidebarProvider, for login page we don't want the sidebar
const useIsSidebarContext = () => {
  try {
    useSidebar();
    return true;
  } catch {
    return false;
  }
};

export const SiteHeader: React.FC<SiteHeaderProps> = ({ className, title }) => {
  const hasSidebarContext = useIsSidebarContext();
  const { has } = useAuth();
  
  const hasClerkPaidPlan = has ? (has({ plan: "pro" }) || has({ plan: "max" })) : false;

  // Set global data attribute on body to control billing visibility
  useEffect(() => {
    const shouldHideBilling = !hasClerkPaidPlan;
    document.body.setAttribute('data-hide-billing', shouldHideBilling.toString());
    
    return () => {
      // Cleanup when component unmounts
      document.body.removeAttribute('data-hide-billing');
    };
  }, [hasClerkPaidPlan]);

  return (
    <header
      className={cn(
        "flex h-16 shrink-0 items-center gap-2 bg-white dark:bg-slate-900 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 z-10",
        className,
      )}
    >
      <div className="flex items-center gap-2 px-4">
        {hasSidebarContext && (
          <>
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
          </>
        )}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage className="text-lg font-medium">
                {title}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="ml-auto flex items-center gap-5 px-4">
        <UserButton
          appearance={{
            elements: {
              avatarBox: "h-8 w-8",
            },
          }}
        />
        <ModeToggle />
      </div>
    </header>
  );
};
